import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { brandColors } from '@utils/color-utils';

interface PositionCloseSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  realizedPnL: number;
  positionCount: number;
  isExpiry?: boolean;
}

export function PositionCloseSuccessModal({
  isOpen,
  onClose,
  realizedPnL,
  positionCount,
  isExpiry = false,
}: PositionCloseSuccessModalProps) {
  const isProfit = realizedPnL > 0;
  const pepeImage = isProfit ? '/pepe-happy.webp' : '/peeposadgecry.webp';
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const formatPnL = (pnl: number) => {
    const absValue = Math.abs(pnl);
    if (absValue >= 1000) {
      return `${pnl >= 0 ? '+' : ''}$${(pnl / 1000).toFixed(1)}k`;
    }
    return `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`;
  };

  useEffect(() => {
    if (!isOpen) {
      // Clear any remaining confetti and timeouts when modal closes
      confetti.reset();
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      return;
    }

    if (isProfit) {
      // Happy confetti rising from bottom like inverse snow
      const colors = [brandColors.green];

      // Create rising confetti effect
      const createRisingConfetti = (delay: number) => {
        const timeout = setTimeout(() => {
          confetti({
            particleCount: 1,
            angle: 90,
            spread: 15,
            origin: { x: Math.random(), y: Math.random() },
            colors,
            gravity: -1, // Negative gravity makes it rise
            scalar: 0.5,
            drift: 0,
            startVelocity: 0,
            ticks: 50,
          });
        }, delay);
        timeoutsRef.current.push(timeout);
      };

      // Create multiple waves of rising confetti
      for (let i = 0; i < 12; i++) {
        createRisingConfetti(i * 200);
      }
    } else {
      // Sad, dramatic snow for losses
      const colors = [brandColors.red];

      // Create slow falling snow effect over 3 seconds
      const createSnowfall = (delay: number) => {
        const timeout = setTimeout(() => {
          confetti({
            particleCount: 1,
            angle: 90,
            spread: 15,
            origin: { x: Math.random(), y: Math.random() },
            colors,
            gravity: 1,
            scalar: 0.35,
            drift: 0,
            startVelocity: 0,
            ticks: 50, // Much longer duration so particles fall completely through the screen
          });
        }, delay);
        timeoutsRef.current.push(timeout);
      };

      // Create multiple waves of snowfall
      for (let i = 0; i < 12; i++) {
        createSnowfall(i * 200);
      }
    }

    // Auto close after 4 seconds
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [isOpen, isProfit, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-brand-dark/90 border border-white/20 rounded-2xl p-8 max-w-sm w-full mx-4 text-center"
            onClick={onClose}
          >
            {/* Pepe Image */}
            <div className="flex justify-center mb-6">
              <img
                src={pepeImage}
                alt={isProfit ? 'Happy Pepe' : 'Sad Pepe'}
                className="w-24 h-24 rounded-full"
              />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-2">
              {isExpiry
                ? isProfit
                  ? `${positionCount} Position${positionCount > 1 ? 's' : ''} Expired In-The-Money!`
                  : `${positionCount} Position${positionCount > 1 ? 's' : ''} Expired Out-Of-The-Money`
                : isProfit
                  ? `${positionCount} Position${positionCount > 1 ? 's' : ''} Closed!`
                  : `${positionCount} Position${positionCount > 1 ? 's' : ''} Closed`}
            </h2>

            {/* PnL Display */}
            <div className="mb-6">
              <div className="text-white/60 text-sm mb-1">Realized PnL</div>
              <div
                className={`text-3xl font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}
              >
                {formatPnL(realizedPnL)}
              </div>
            </div>

            {/* Tap to dismiss hint */}
            <div className="text-white/40 text-xs mt-4">
              Tap anywhere to dismiss
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
